<template>
  <el-dialog
    :visible.sync="dialogVisible"
    :width="dialogWidth"
    append-to-body
    custom-class="qr-code-dialog"
    :close-on-click-modal="true"
    :close-on-press-escape="true"
    :show-close="true"
    @open="handleOpen"
    @closed="handleClosed"
  >
    <div class="qr-dialog__body">
      <div class="qr-dialog__header">
        <div class="qr-dialog__title">{{ title || '扫码访问' }}</div>
        <div class="qr-dialog__desc">请使用手机扫码访问，或复制链接在移动端打开</div>
      </div>

      <div class="qr-dialog__content" :style="contentStyle">
        <div ref="canvasWrap" class="qr-dialog__canvas-wrap" :style="canvasWrapStyle">
          <canvas ref="qrCanvas" class="qr-dialog__canvas" :aria-label="title || '二维码'"></canvas>
        </div>

        <div class="qr-dialog__info">
          <div class="qr-dialog__url" v-if="showUrl">
            <span class="qr-dialog__url-label">链接</span>
            <span class="qr-dialog__url-text">{{ finalUrl }}</span>
          </div>

          <div class="qr-dialog__tips">
            <div>移动端可直接扫码访问或长按保存。</div>
            <div>电脑端可复制链接，或下载 PNG 二维码。</div>
          </div>

          <div class="qr-dialog__actions">
            <el-button size="medium" @click="handleCopyUrl" :disabled="!finalUrl">复制链接</el-button>
            <el-button type="primary" size="medium" @click="handleDownload" :disabled="!qrDataUrl">
              下载二维码
            </el-button>
          </div>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import QRCode from 'qrcode'

function normalizeUrl(input) {
  if (!input) return ''
  return /^https?:\/\//i.test(input) ? input : `https://${input}`
}

function getViewportWidth() {
  if (typeof window === 'undefined') return 1024

  const visualWidth = Number(window.visualViewport && window.visualViewport.width)
  if (Number.isFinite(visualWidth) && visualWidth > 0) {
    return Math.floor(visualWidth)
  }

  const innerWidth = Number(window.innerWidth)
  if (Number.isFinite(innerWidth) && innerWidth > 0) {
    return Math.floor(innerWidth)
  }

  const docWidth = Number(document.documentElement && document.documentElement.clientWidth)
  if (Number.isFinite(docWidth) && docWidth > 0) {
    return Math.floor(docWidth)
  }

  return 1024
}

export default {
  name: 'QrCodeDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    url: {
      type: String,
      default: ''
    },
    title: {
      type: String,
      default: '扫码访问'
    },
    size: {
      type: Number,
      default: 240
    },
    errorCorrectionLevel: {
      type: String,
      default: 'M'
    },
    showUrl: {
      type: Boolean,
      default: true
    },
    fileName: {
      type: String,
      default: 'qrcode'
    },
    fallbackToCurrentPage: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      dialogVisible: false,
      qrDataUrl: '',
      isRendering: false,
      pendingRender: false,
      viewportWidth: getViewportWidth(),
      canvasWrapWidth: 0,
      resizeObserver: null
    }
  },
  computed: {
    isMobileViewport() {
      return this.viewportWidth <= 768
    },
    finalUrl() {
      const input = (this.url || '').trim()

      if (input) {
        return normalizeUrl(input)
      }

      if (this.fallbackToCurrentPage && typeof window !== 'undefined') {
        return window.location.href
      }

      return ''
    },
    qrRenderSize() {
      const desktopBaseSize = Math.max(this.size, 300)
      const mobileBaseSize = Math.min(this.size, 220)
      const baseSize = this.isMobileViewport ? mobileBaseSize : desktopBaseSize
      const minSize = this.isMobileViewport ? 150 : 220
      const wrapPadding = this.isMobileViewport ? 24 : 36
      const availableWidth = this.canvasWrapWidth
        ? Math.max(minSize, Math.floor(this.canvasWrapWidth - wrapPadding))
        : baseSize

      return Math.max(minSize, Math.min(baseSize, availableWidth))
    },
    qrWrapSize() {
      return this.qrRenderSize + 64
    },
    dialogWidth() {
      if (this.isMobileViewport) {
        return '92%'
      }

      return `${Math.max(720, this.qrWrapSize + 300)}px`
    },
    contentStyle() {    
      if (this.isMobileViewport) {
        return null
      }

      return {
        gridTemplateColumns: `${this.qrWrapSize}px minmax(0, 1fr)`
      }
    },
    canvasWrapStyle() {
      if (this.isMobileViewport) {
        return null
      }

      return {
        minHeight: `${this.qrWrapSize}px`
      }
    }
  },
  mounted() {
    if (typeof window === 'undefined') return

    window.addEventListener('resize', this.handleViewportChange, { passive: true })
    window.addEventListener('orientationchange', this.handleViewportChange, { passive: true })
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.handleViewportChange, { passive: true })
    }
    this.$nextTick(() => {
      this.setupResizeObserver()
      this.measureCanvasWrap()
    })
  },
  beforeDestroy() {
    if (typeof window === 'undefined') return

    window.removeEventListener('resize', this.handleViewportChange)
    window.removeEventListener('orientationchange', this.handleViewportChange)
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.handleViewportChange)
    }
    this.teardownResizeObserver()
  },
  watch: {
    visible: {
      immediate: true,
      handler(val) {
        this.dialogVisible = val
      }
    },
    dialogVisible(val) {
      this.$emit('update:visible', val)
      this.$emit(val ? 'open' : 'close')

      if (val) {
        this.$nextTick(() => {
          this.renderQrCode()
        })
      }
    },
    finalUrl() {
      if (this.dialogVisible) {
        this.$nextTick(() => {
          this.renderQrCode()
        })
      }
    },
    size() {
      if (this.dialogVisible) {
        this.$nextTick(() => {
          this.renderQrCode()
        })
      }
    },
    errorCorrectionLevel() {
      if (this.dialogVisible) {
        this.$nextTick(() => {
          this.renderQrCode()
        })
      }
    }
  },
  methods: {
    requestRender() {
      if (!this.dialogVisible) return

      this.$nextTick(() => {
        this.renderQrCode()
      })
    },
    setupResizeObserver() {
      this.teardownResizeObserver()

      if (typeof ResizeObserver === 'undefined' || !this.$refs.canvasWrap) {
        return
      }

      this.resizeObserver = new ResizeObserver(() => {
        this.measureCanvasWrap()
      })
      this.resizeObserver.observe(this.$refs.canvasWrap)
    },
    teardownResizeObserver() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
        this.resizeObserver = null
      }
    },
    measureCanvasWrap() {
      const wrap = this.$refs.canvasWrap
      if (!wrap) return

      const nextWidth = Math.floor(wrap.clientWidth || 0)
      if (!nextWidth || nextWidth === this.canvasWrapWidth) return

      this.canvasWrapWidth = nextWidth

      this.requestRender()
    },
    handleViewportChange() {
      this.viewportWidth = getViewportWidth()

      this.$nextTick(() => {
        this.measureCanvasWrap()
      })

      this.requestRender()
    },
    async handleOpen() {
      this.handleViewportChange()
      await this.$nextTick()
      this.setupResizeObserver()
      this.measureCanvasWrap()
      this.requestRender()
    },
    handleClosed() {},
    async renderQrCode() {
      if (!this.finalUrl || !this.$refs.qrCanvas) {
        return
      }

      if (this.isRendering) {
        this.pendingRender = true
        return
      }

      this.isRendering = true
      this.pendingRender = false

      try {
        const canvas = this.$refs.qrCanvas
        const dpr = window.devicePixelRatio || 1
        const renderSize = this.qrRenderSize

        canvas.width = renderSize * dpr
        canvas.height = renderSize * dpr
        canvas.style.width = `${renderSize}px`
        canvas.style.height = `${renderSize}px`

        const options = {
          errorCorrectionLevel: this.errorCorrectionLevel,
          margin: 2,
          width: renderSize * dpr,
          color: {
            dark: '#111111',
            light: '#FFFFFF'
          }
        }

        await QRCode.toCanvas(canvas, this.finalUrl, options)

        this.qrDataUrl = await QRCode.toDataURL(this.finalUrl, {
          errorCorrectionLevel: this.errorCorrectionLevel,
          margin: 2,
          width: 960,
          color: {
            dark: '#111111',
            light: '#FFFFFF'
          }
        })
      } catch (error) {
        console.error('二维码生成失败：', error)
        this.qrDataUrl = ''
        this.$message.error('二维码生成失败，请检查链接是否正确')
      } finally {
        this.isRendering = false

        if (this.pendingRender) {
          this.pendingRender = false
          this.requestRender()
        }
      }
    },
    handleDownload() {
      if (!this.qrDataUrl) {
        this.$message.warning('二维码尚未生成')
        return
      }

      const link = document.createElement('a')
      link.href = this.qrDataUrl
      link.download = `${this.normalizeFileName(this.fileName)}.png`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    async handleCopyUrl() {
      if (!this.finalUrl) {
        this.$message.warning('暂无可复制的链接')
        return
      }

      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(this.finalUrl)
          this.$message.success('链接已复制')
          return
        }

        this.fallbackCopyText(this.finalUrl)
        this.$message.success('链接已复制')
      } catch (error) {
        console.error('复制失败：', error)
        this.$message.error('复制失败，请手动复制')
      }
    },
    fallbackCopyText(text) {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.setAttribute('readonly', 'readonly')
      textarea.style.position = 'fixed'
      textarea.style.top = '-9999px'
      textarea.style.left = '-9999px'
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
    },
    normalizeFileName(name) {
      return String(name || 'qrcode')
        .replace(/[\\/:*?"<>|]/g, '_')
        .replace(/\s+/g, '_')
    }
  }
}
</script>

<style scoped>
.qr-dialog__body {
  padding: 4px 0 8px;
}

.qr-dialog__header {
  margin-bottom: 18px;
  text-align: center;
}

.qr-dialog__title {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  line-height: 1.4;
}

.qr-dialog__desc {
  margin-top: 6px;
  font-size: 13px;
  color: #909399;
  line-height: 1.5;
}

.qr-dialog__content {
  display: grid;
  grid-template-columns: 364px minmax(0, 1fr);
  gap: 18px;
  align-items: stretch;
}

.qr-dialog__canvas-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 364px;
  padding: 18px;
  border-radius: 16px;
  background: #f7f8fa;
  border: 1px solid #eef1f5;
  overflow: hidden;
  box-sizing: border-box;
}

.qr-dialog__canvas {
  display: block;
  max-width: 100%;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
  touch-action: manipulation;
}

.qr-dialog__info {
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: center;
}

.qr-dialog__url {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
  background: #fafafa;
  border-radius: 12px;
  border: 1px solid #eef1f5;
  word-break: break-all;
  line-height: 1.6;
}

.qr-dialog__url-label {
  color: #606266;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
}

.qr-dialog__url-text {
  color: #409eff;
  font-size: 13px;
}

.qr-dialog__tips {
  padding: 12px 14px;
  font-size: 12px;
  color: #909399;
  line-height: 1.7;
  background: #fcfcfd;
  border: 1px solid #eef1f5;
  border-radius: 12px;
}

.qr-dialog__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.qr-dialog__actions .el-button {
  min-width: 120px;
  margin-left: 0;
}

@media screen and (max-width: 768px) {
  .qr-dialog__body {
    max-height: calc(100vh - 104px);
    overflow-y: auto;
    overflow-x: hidden;
  }

  .qr-dialog__title {
    font-size: 16px;
  }

  .qr-dialog__desc {
    font-size: 12px;
  }

  .qr-dialog__content {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .qr-dialog__canvas-wrap {
    min-height: auto;
    padding: 12px;
    border-radius: 14px;
  }

  .qr-dialog__canvas {
    margin: 0 auto;
    max-width: 100%;
  }

  .qr-dialog__actions {
    flex-direction: column;
  }

  .qr-dialog__actions .el-button {
    width: 100%;
  }
}
</style>

<style>
.qr-code-dialog {
  border-radius: 16px;
  overflow: hidden;
}

.qr-code-dialog .el-dialog__header {
  padding: 16px 16px 0;
}

.qr-code-dialog .el-dialog__body {
  padding: 12px 16px 20px;
}

@media screen and (max-width: 768px) {
  .qr-code-dialog {
    margin-top: 8vh !important;
    width: 92% !important;
    max-width: 560px;
  }

  .qr-code-dialog .el-dialog__header {
    padding: 12px 12px 0;
  }

  .qr-code-dialog .el-dialog__body {
    padding: 10px 12px 16px;
  }
}
</style>
